import '../styles/pages.scss';
import {useEffect, useRef, useState} from "react";
import {useLocation} from 'react-router-dom';
import {
    getFirstBar,
    getGapPercentage,
    getSecondBar,
    getSelectedSecurities,
    getStrategies, getTradeData,
    checkHealth
} from "../lib/beCall";
import {Box, Flex, Spinner, Center, Text, Alert, AlertIcon} from "@chakra-ui/react";
import Toolbar from "../components/toolbar/toolbar";
import StrategySidebar from "../components/strategySidebar/strategySidebar";
import StrategyContent from "../components/strategyContent/strategyContent";
import OfflineScreen from "../components/offlineScreen/offlineScreen";
import {useDate} from "../contexts/DateContext";


const dashboardCache = new Map();

const Dashboard = () => {
    const location = useLocation();
    const {
        selectedDate: lastDay,
        setSelectedDate: setLastDay,
        isPolling,
        setIsPolling,
        selectedStrategy: savedStrategy,
        setSelectedStrategy: setSavedStrategy,
        consecutiveErrors,
        setConsecutiveErrors,
        hasConnectedOnce,
        setHasConnectedOnce
    } = useDate();
    const MAX_CONSECUTIVE_ERRORS = 50;
    const [strategies, setStrategies] = useState([]);
    const [selectedStrategy, setSelectedStrategy] = useState(0);
    const refIndex = useRef(0);
    const [stepIndex, setStepIndex] = useState(0);
    const [stepsData, setStepsData] = useState([]);
    const [selectedTags, setSelectedTags] = useState({});
    const [isOffline, setIsOffline] = useState(false);
    const [isSlowConnection, setIsSlowConnection] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [nextRetryIn, setNextRetryIn] = useState(0);
    const retryTimeoutRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const isHandlingOfflineRef = useRef(false);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            const data = await getStrategies();
            if (data) {
                // Setta la data SOLO se non è già presente nel Context
                if (!lastDay) {
                    setLastDay(data['date']);
                }
                const strategiesList = data['strategies'];
                setStrategies(strategiesList);

                // Determina quale strategia selezionare
                if (strategiesList && strategiesList.length > 0) {
                    if (savedStrategy !== null) {
                        // Se c'è una strategia salvata in sessionStorage, usa quella
                        const strategyName = savedStrategy;
                        const strategyIndex = strategiesList.indexOf(strategyName);
                        if (strategyIndex !== -1) {
                            setSelectedStrategy(strategyIndex);
                        } else {
                            // Se la strategia salvata non esiste più, seleziona la prima alfabetica
                            const sortedStrategies = [...strategiesList].sort((a, b) =>
                                a.toLowerCase().localeCompare(b.toLowerCase())
                            );
                            const firstAlphabetic = sortedStrategies[0];
                            const indexInOriginal = strategiesList.indexOf(firstAlphabetic);
                            setSelectedStrategy(indexInOriginal);
                            setSavedStrategy(firstAlphabetic);
                        }
                    } else {
                        // Prima volta: seleziona la prima in ordine alfabetico
                        const sortedStrategies = [...strategiesList].sort((a, b) =>
                            a.toLowerCase().localeCompare(b.toLowerCase())
                        );
                        const firstAlphabetic = sortedStrategies[0];
                        const indexInOriginal = strategiesList.indexOf(firstAlphabetic);
                        setSelectedStrategy(indexInOriginal);
                        setSavedStrategy(firstAlphabetic);
                    }
                }

                setIsOffline(false);
            } else {
                handleOffline();
            }
        } catch (error) {
            console.error('Failed to load initial data:', error);
            handleOffline();
        }
    };

    const handleOffline = async () => {
        if (isHandlingOfflineRef.current) return;
        isHandlingOfflineRef.current = true;
        try {
            const alive = await checkHealth();
            if (alive) {
                setIsSlowConnection(true);
            } else {
                setIsOffline(true);
                scheduleRetry();
            }
        } finally {
            isHandlingOfflineRef.current = false;
        }
    };

    const handleOnline = () => {
        consecutiveErrorsRef.current = 0;
        setConsecutiveErrors(0);
        hasConnectedOnceRef.current = true;
        setHasConnectedOnce(true);
        setIsOffline(false);
        setIsSlowConnection(false);
        clearRetryTimeout();
    };

    const scheduleRetry = () => {
        clearRetryTimeout();

        // Countdown da 30 secondi
        setNextRetryIn(30);
        countdownIntervalRef.current = setInterval(() => {
            setNextRetryIn(prev => {
                if (prev <= 1) {
                    clearInterval(countdownIntervalRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        // Riprova dopo 30 secondi
        retryTimeoutRef.current = setTimeout(() => {
            loadInitialData();
        }, 30000);
    };

    const clearRetryTimeout = () => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        setNextRetryIn(0);
    };

    const handleManualRetry = () => {
        clearRetryTimeout();
        loadInitialData();
    };

    const intervalRef = useRef(null);
    const isPollingRef = useRef(isPolling);
    const abortControllerRef = useRef(null);
    const consecutiveErrorsRef = useRef(consecutiveErrors);
    const hasConnectedOnceRef = useRef(hasConnectedOnce);
    const prevDataKeyRef = useRef(null);
    const dataCacheRef = useRef([null, null, null, null]);
    const currentStepsDataRef = useRef([]);

    // Mantieni le ref sincronizzate con gli state
    useEffect(() => {
        isPollingRef.current = isPolling;
    }, [isPolling]);

    useEffect(() => {
        consecutiveErrorsRef.current = consecutiveErrors;
    }, [consecutiveErrors]);

    useEffect(() => {
        hasConnectedOnceRef.current = hasConnectedOnce;
    }, [hasConnectedOnce]);

    useEffect(() => {
        let cancelled = false;

        const clearCurrentTimeout = () => {
            if (intervalRef.current) {
                clearTimeout(intervalRef.current);
                intervalRef.current = null;
            }
        };

        const abortCurrentRequests = () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };

        // Pulisci sempre il timeout precedente e cancella le richieste
        clearCurrentTimeout();
        abortCurrentRequests();

        if (!lastDay || strategies.length === 0) return;

        const strategy = strategies[selectedStrategy];
        const dataKey = `${lastDay}-${strategy}`;
        const dataParamsChanged = prevDataKeyRef.current !== dataKey;
        prevDataKeyRef.current = dataKey;

        if (dataParamsChanged) {
            consecutiveErrorsRef.current = 0;
            setConsecutiveErrors(0);
            const cached = dashboardCache.get(dataKey);
            if (cached) {
                setStepsData(cached.stepsData);
                currentStepsDataRef.current = cached.stepsData;
                dataCacheRef.current = [...cached.serialized];
                refIndex.current = cached.refIndexVal;
                setStepIndex(cached.refIndexVal);
                setIsLoadingData(false);
                setIsRefreshing(true);
            } else {
                setStepsData([]);
                currentStepsDataRef.current = [];
                dataCacheRef.current = [null, null, null, null];
                refIndex.current = 0;
                setIsLoadingData(true);
                setHasFetched(false);
                setIsRefreshing(false);
            }
        }

        // Aggiorna uno step solo se i dati sono effettivamente cambiati
        const updateStepIfChanged = (stepIdx, newEntry) => {
            const serialized = JSON.stringify(newEntry.table);
            if (serialized !== dataCacheRef.current[stepIdx]) {
                dataCacheRef.current[stepIdx] = serialized;
                setStepsData(prev => {
                    const updated = [...prev];
                    updated[stepIdx] = newEntry;
                    currentStepsDataRef.current = updated;
                    return updated;
                });
            }
        };

        const pollingFn = async () => {
            // Crea un nuovo AbortController per questo ciclo di polling
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            try {
                let message = '';
                let table = [];
                if (refIndex.current < 2) {
                    message = await getSelectedSecurities({strategy, day: lastDay}, signal);
                    table = await getGapPercentage({strategy, day: lastDay}, signal);
                    if (table && table.length > 0) {
                        updateStepIfChanged(0, {
                            message: JSON.parse(JSON.stringify(message)),
                            table: table.map(item => ({...item}))
                        });
                        if (refIndex.current < 1) {
                            refIndex.current = 1;
                            setStepIndex(1);
                        }
                    }
                }

                if (refIndex.current < 3) {
                    table = await getFirstBar({strategy, day: lastDay}, signal);
                    if (table && table.length > 0) {
                        updateStepIfChanged(1, {
                            message: {securities: [...new Set(table.map(item => item.security))]},
                            table: table.map(item => ({...item}))
                        });
                        if (refIndex.current < 2) {
                            refIndex.current = 2;
                            setStepIndex(2);
                        }
                    }

                    table = await getSecondBar({strategy, day: lastDay}, signal);
                    if (table && table.length > 0) {
                        updateStepIfChanged(2, {
                            message: {securities: [...new Set(table.map(item => item.security))]},
                            table: table.map(item => ({...item}))
                        });
                        if (refIndex.current < 3) {
                            refIndex.current = 3;
                            setStepIndex(3);
                        }
                    }
                }

                table = await getTradeData({strategy, day: lastDay}, signal);
                if (table && table.length > 0) {
                    updateStepIfChanged(3, {
                        message: {securities: [...new Set(table.map(item => item.security))]},
                        table: table.map(item => ({...item}))
                    });
                    if (refIndex.current < 4) {
                        refIndex.current = 4;
                        setStepIndex(4);
                    }
                }

                // Dati caricati con successo — salva in cache per stale-while-revalidate
                if (currentStepsDataRef.current.length > 0) {
                    dashboardCache.set(dataKey, {
                        stepsData: currentStepsDataRef.current,
                        serialized: [...dataCacheRef.current],
                        refIndexVal: refIndex.current,
                    });
                }
                setIsLoadingData(false);
                setHasFetched(true);
                setIsRefreshing(false);
                handleOnline();
            } catch (error) {
                if (cancelled) return;
                if (error.name === 'CanceledError' || error.name === 'AbortError') {
                    console.log('Polling requests canceled');
                } else {
                    setIsLoadingData(false);

                    // Se non c'è mai stata una connessione riuscita, vai offline subito
                    if (!hasConnectedOnceRef.current) {
                        console.error('Error in polling (server not available):', error);
                        handleOffline();
                        return;
                    }

                    // Altrimenti usa il sistema di protezione
                    consecutiveErrorsRef.current += 1;
                    setConsecutiveErrors(consecutiveErrorsRef.current);
                    console.error(`Error in polling (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS}):`, error);

                    if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
                        handleOffline();
                    }
                }
            }
        };

        const scheduleNextPoll = () => {
            if (cancelled || !isPollingRef.current || location.pathname !== '/') return;
            intervalRef.current = setTimeout(async () => {
                if (cancelled) return;
                intervalRef.current = null;
                await pollingFn();
                scheduleNextPoll();
            }, 3000);
        };

        if (dataParamsChanged) {
            // Parametri dati cambiati: fetch immediato poi scheduling
            pollingFn().then(() => {
                scheduleNextPoll();
            }).catch((error) => {
                if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
                    console.error('Error in initial polling:', error);
                }
            });
        } else {
            // Solo isPolling o pathname cambiati: riavvia scheduling senza resettare i dati
            scheduleNextPoll();
        }

        return () => {
            cancelled = true;
            clearCurrentTimeout();
            abortCurrentRequests();
        };
    }, [lastDay, strategies, selectedStrategy, isPolling, location.pathname]);

    // Cleanup dei timeout quando il componente si smonta
    useEffect(() => {
        return () => {
            clearRetryTimeout();
        };
    }, []);

    // Wrapper per setSelectedStrategy che salva anche nel Context
    const handleStrategyChange = (index) => {
        setSelectedStrategy(index);
        if (strategies && strategies[index]) {
            setSavedStrategy(strategies[index]);
        }
    };

    // Se il sistema è offline, mostra la schermata offline
    if (isOffline) {
        return <OfflineScreen onRetry={handleManualRetry} nextRetryIn={nextRetryIn}/>;
    }

    return (
        <Flex direction="column" h="100vh" overflow="hidden">
            {isSlowConnection && (
                <Alert status="warning" py={2}>
                    <AlertIcon />
                    <Text fontSize="sm">Backend lento — ricaricamento in corso...</Text>
                </Alert>
            )}
            {isRefreshing && !isSlowConnection && (
                <Alert status="info" py={2}>
                    <AlertIcon />
                    <Text fontSize="sm">Aggiornamento dati in corso...</Text>
                </Alert>
            )}
            <Box>
                <Toolbar last_day={lastDay} setDate={setLastDay}/>
            </Box>
            <Flex flex="1" overflow="hidden">
                <StrategySidebar
                    strategies={strategies}
                    selectedStrategy={selectedStrategy}
                    setSelectedStrategy={handleStrategyChange}
                />
                <Box flex="1" overflow="auto">
                    {(isLoadingData || !hasFetched) ? (
                        <Center h="100%">
                            <Flex direction="column" align="center" gap={4}>
                                <Spinner
                                    thickness="4px"
                                    speed="0.65s"
                                    emptyColor="gray.200"
                                    color="#38A169"
                                    size="xl"
                                />
                                <Text color="gray.600" fontSize="md">Caricamento dati in corso...</Text>
                            </Flex>
                        </Center>
                    ) : stepsData.length === 0 ? (
                        <Center h="100%">
                            <Text color="gray.500" fontSize="md">Nessun dato trovato per questa data</Text>
                        </Center>
                    ) : (
                        <StrategyContent
                            strategies={strategies}
                            selectedStrategy={selectedStrategy}
                            stepIndex={stepIndex}
                            stepsData={stepsData}
                            selectedTags={selectedTags}
                            setSelectedTags={setSelectedTags}
                        />
                    )}
                </Box>
            </Flex>
        </Flex>
    );
};

export default Dashboard;